/**
 * The HOC to provide Location.
 *
 * Injects the current location as `location` prop to the wrapped component.
 * This HOC assumes that your componoent is using exact props.  Please update
 * your component to use exact props before using this HOC.
 *
 * Note that this could be used out of the AppShell and in that case, the
 * location is derived from `window.location`
 * TODO(jangmi, WEB-1642): Remove this paragraph once we migrate everything to
 * AppShell as it won't be the case any longer.
 */

import * as React from "react";

import {InitialRequestUrl} from "@khan/shared-core";
import {AppShellContext} from "@khan/shared-core/context";
import {useLocation, type Location} from "@khan/urls";

type LocationProviderProps = {
    children: (location: Location) => React.ReactNode;
};

/* This is a LocationProvider that will be used as a fallback when the React
 * React Router is not present in the DOM. We use this when the AppShell
 * context has inAppShell: false.
 * TODO(jangmi, WEB-1642): Remove WindowLocationProvider once we migrate
 * everything to AppShell as we don't need fallback any longer.
 */
class WindowLocationProvider extends React.Component<LocationProviderProps> {
    render(): React.ReactNode {
        const {href, protocol, host, hostname, port, pathname, search, hash} =
            window.location; // eslint-disable-line no-restricted-syntax

        return this.props.children({
            href,
            protocol,
            host,
            hostname,
            port,
            pathname,
            search,
            hash,
            origin: InitialRequestUrl.origin,
            get queryParams() {
                const params = new URLSearchParams(search);
                // NOTE(somewhatabstract): This object is really
                // Record<string, string | Array<string>> but the original
                // function we used (that also built an object like this) had
                // incorrect typing so we're maintaining that for
                // now to make updating simpler.
                const paramsAsObject = Object.fromEntries(params.entries());
                return paramsAsObject;
            },
            state: undefined,
        });
    }
}

/* This is a withRouter wrapped LocationProvider that uses the React Router's
 * location to craft the current location.
 */
const RouterLocationProvider = (
    props: LocationProviderProps,
): React.ReactElement => {
    const location = useLocation();
    return <>{props.children(location)}</>;
};

export type WithLocationProps = {
    location: Location;
};

export type WithoutLocation<T> = Omit<T, keyof WithLocationProps>;

const withLocation = <
    C extends React.ComponentType<
        React.ComponentProps<C> & WithLocationProps
    > & {
        displayName?: string | undefined;
    },
    OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithLocationProps
    >,
>(
    WrappedComponent: C,
): React.ComponentType<OuterProps> => {
    // Creating the inner component. The calculated Props type here is the where the magic happens.
    const ComponentWithLocation = (props: OuterProps): React.ReactElement => {
        return (
            <AppShellContext.Consumer>
                {({inAppShell}) => {
                    const LocationProvider = inAppShell
                        ? RouterLocationProvider
                        : WindowLocationProvider;

                    return (
                        <LocationProvider>
                            {(location) => (
                                <WrappedComponent
                                    {...(props as any)}
                                    location={location}
                                />
                            )}
                        </LocationProvider>
                    );
                }}
            </AppShellContext.Consumer>
        );
    };

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    ComponentWithLocation.displayName = `withLocation(${displayName})`;

    return ComponentWithLocation;
};

export default withLocation;
